import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Eye, CheckCircle2, Upload } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent } from '../ui/card';
import { lettersAPI, RISK_COLOR, errText, openBlob } from '../../lib/lettersAPI';

const DocxUpload = ({ onDone }) => {
  const [f, setF] = useState({ name: '', category: 'General', file: null });
  const go = async () => { const fd = new FormData(); fd.append('name', f.name); fd.append('category', f.category); fd.append('file', f.file);
    try { await lettersAPI.createDocxTemplate(fd); toast.success('DOCX template uploaded (pending approval)'); onDone(); } catch (e) { toast.error(errText(e)); } };
  return (
    <Card><CardContent className="flex flex-wrap items-center gap-2 p-3" data-testid="letter-docx-upload">
      <Upload className="h-4 w-4" /><Input placeholder="Template name" className="max-w-xs" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="letter-docx-name" />
      <Input placeholder="Category" className="max-w-[180px]" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} />
      <Input type="file" accept=".docx" className="max-w-xs" onChange={(e) => setF({ ...f, file: e.target.files?.[0] || null })} data-testid="letter-docx-file" />
      <Button size="sm" disabled={!f.file || f.name.length < 3} onClick={go} data-testid="letter-docx-submit">Upload DOCX template</Button>
      <span className="text-xs text-muted-foreground">Use {'{{merge_fields}}'}; converted to PDF with LibreOffice.</span>
    </CardContent></Card>
  );
};

export const LetterTemplatesTab = ({ meta }) => {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState('');
  const admin = ['owner', 'admin'].includes(meta?.me?.role);
  const load = useCallback(() => lettersAPI.templates({ q }).then(setRows).catch(() => {}), [q]);
  useEffect(() => { load(); }, [load]);
  const act = async (fn, ok) => { try { await fn(); toast.success(ok); load(); } catch (e) { toast.error(errText(e)); } };
  const cats = [...new Set(rows.map((r) => r.category))];
  return (
    <div className="space-y-4" data-testid="letter-templates-tab">
      <div className="flex flex-wrap items-center gap-2">
        <Input placeholder="Search templates" className="max-w-sm" value={q} onChange={(e) => setQ(e.target.value)} data-testid="letter-templates-search" />
        <p className="text-xs text-muted-foreground">Seeded wording is a draft and must be approved by the practice before use.</p>
      </div>
      {admin && <DocxUpload onDone={load} />}
      {cats.map((c) => (
        <section key={c}>
          <p className="mb-2 text-sm font-semibold">{c}</p>
          <div className="divide-y rounded-md border">
            {rows.filter((r) => r.category === c).map((t) => {
              const pending = t.versions.filter((v) => v.status !== 'approved');
              return (
                <div key={t.id} className="flex flex-wrap items-center gap-2 p-3 text-sm" data-testid={`letter-template-${t.seed_key || t.id}`}>
                  <div className="min-w-[240px] flex-1"><p className="font-medium">{t.name}</p><p className="text-xs text-muted-foreground">{t.description}{t.evidence?.length ? ` · Evidence: ${t.evidence.join('; ')}` : ''}</p></div>
                  <Badge className={RISK_COLOR[t.risk]}>{t.risk}</Badge>
                  <Badge className={t.active ? 'bg-emerald-100 text-emerald-800' : 'bg-muted text-muted-foreground'}>{t.active ? `Active v${t.current_version}` : 'Draft – requires practice approval'}</Badge>
                  <Button size="sm" variant="ghost" onClick={async () => openBlob(await lettersAPI.templatePreview(t.id))} data-testid={`letter-template-preview-${t.seed_key || t.id}`}><Eye className="h-4 w-4" /></Button>
                  {admin && pending.map((v) => <Button key={v.version} size="sm" variant="outline" onClick={() => act(() => lettersAPI.approveVersion(t.id, v.version), `Approved v${v.version}`)} data-testid={`letter-template-approve-${t.seed_key || t.id}-${v.version}`}><CheckCircle2 className="mr-1 h-3.5 w-3.5" />Approve v{v.version}</Button>)}
                  {admin && t.current_version && <Button size="sm" variant="ghost" onClick={() => act(() => lettersAPI.setActive(t.id, !t.active), t.active ? 'Deactivated' : 'Activated')} data-testid={`letter-template-toggle-${t.seed_key || t.id}`}>{t.active ? 'Deactivate' : 'Activate'}</Button>}
                </div>);
            })}
          </div>
        </section>))}
    </div>
  );
};
